<template>
  <div class="aiw2-seo">
    <hero title="SEO & quality review" :keyword="keyword" :mode="mode" :page-run="pageRun" :meta-items="metaItems"
      info-title="POP re-reads the finished draft and scores it, then lists what would make it stronger. You choose which of those changes to apply." />

    <p class="intro">POP checked your draft for score, readability, structure, and page experience.</p>

    <div v-if="analyzing" class="loading-card"><span class="spin"></span> Scoring your article…</div>

    <template v-else>
      <!-- Scores -->
      <div class="score-strip">
        <score-card label="POP score" :value="popScore" :meta="popMeta" :color="popColor" />
        <score-card label="Page experience" :value="pageExp" :meta="pageExpMeta" :color="pageColor" />
      </div>

      <!-- Locked-section protection -->
      <div class="protect-banner">
        <span class="ico">🔒</span>
        <span>POP will preserve locked sections and your manual edits.</span>
      </div>

      <!-- Applying-fixes progress -->
      <div v-if="fixApplying" class="fixprog">
        <div class="fixprog-head">
          <h4>Applying {{ fixSteps.length }} fix{{ fixSteps.length === 1 ? '' : 'es' }}…</h4>
          <span class="fixprog-pct">{{ fixProgress.pct }}%</span>
        </div>
        <div class="fixprog-sub">POP is rewriting the draft. Locked sections and your edits are preserved.</div>
        <div class="fixprog-track"><div class="fixprog-fill" :style="{ width: fixProgress.pct + '%' }"></div></div>
        <ul class="fixprog-steps">
          <li v-for="(s, i) in fixSteps" :key="i" :class="stepState(i)"><span class="dot"></span>{{ s }}</li>
        </ul>
      </div>

      <!-- Recommended fixes (selectable) -->
      <template v-if="fixableIssues.length">
        <div class="section-label">{{ fixableIssues.length }} suggested fix{{ fixableIssues.length === 1 ? '' : 'es' }}</div>
        <ul class="issue-list">
          <issue-card
            v-for="d in displayIssues"
            :key="d.issue.issue_id"
            :issue="d.issue"
            :fixed="d.fixed"
            :selected="!d.fixed && isSelected(d.issue.issue_id)"
            :applying="fixApplying && !d.fixed && isSelected(d.issue.issue_id)"
            @toggle-select="toggleSelect"
          />
        </ul>

        <!-- Polish style (applies when the Polish fix is selected) -->
        <div class="polish">
          <div class="polish-title">Polish style</div>
          <div class="help-text">Premium polish can rewrite for flow. Score-safe polish keeps your SEO terms locked in.</div>
          <div class="polish-style-grid">
            <button v-for="p in POLISH_STYLES" :key="p.value" class="polish-style-card" :class="{ selected: polishStyle === p.value }" @click="polishStyle = p.value">
              <span v-if="p.value === 'score_safe'" class="rec-tag">Default</span>
              <div class="name">{{ p.label }}</div>
              <div class="desc">{{ p.desc }}</div>
            </button>
          </div>
        </div>

        <!-- Fix bar: selection summary + presets + apply -->
        <div class="fix-bar">
          <div class="fix-bar-col">
            <div class="fix-bar-sum"><b>{{ selectedCount }} fix{{ selectedCount === 1 ? '' : 'es' }}</b> selected · <b>{{ costLabel }}</b> · 1 run</div>
            <div class="fix-budget">
              <span>Fix runs used <b>{{ fixRunsUsed }}</b> / {{ fixRunLimit }} free</span>
              <span class="fix-budget-track"><span class="fix-budget-fill" :style="{ width: fixRunsPct + '%' }"></span></span>
            </div>
          </div>
          <div class="fix-bar-actions">
            <div class="fix-presets">
              <button v-for="p in PRESETS" :key="p.key" class="fix-preset" @click="applyPreset(p.key)">{{ p.label }}</button>
            </div>
            <button class="seo-btn btn-fix-all" :disabled="busy || !selectedCount" @click="openApply">{{ applyLabel }}</button>
          </div>
        </div>
      </template>
      <div v-else class="all-good"><span class="ico">✓</span> No outstanding issues - you're ready to publish.</div>

      <!-- Applied fixes -->
      <div v-if="lastFix" class="applied-card">
        <h4>✓ Fixes applied</h4>
        <ul class="applied-list">
          <li v-for="(f, i) in lastFix.applied_fixes" :key="i"><span class="ico">✓</span> {{ f }}</li>
          <li v-for="(s, i) in lastFix.skipped_locked_blocks" :key="'s' + i"><span class="ico">↷</span> Skipped your locked section: <em>{{ s.label }}</em></li>
        </ul>
        <button class="seo-btn btn-soft btn-xs view-diff" @click="openDiff">View before / after diff</button>
      </div>

      <!-- Quality review -->
      <div class="quality-head">Quality review</div>
      <div class="quality-card">
        <div class="quality-section">
          <h5 class="good">What's working</h5>
          <ul class="quality-list">
            <li v-for="(g, i) in whatsWorking" :key="i"><span class="ico good">✓</span> {{ g }}</li>
          </ul>
        </div>
        <div class="quality-section">
          <h5 class="work">Needs improvement</h5>
          <ul class="quality-list">
            <li v-for="(w, i) in needsImprovement" :key="i"><span class="ico work">⚠</span> {{ w }}</li>
            <li v-if="!needsImprovement.length"><span class="ico good">✓</span> No quality concerns flagged.</li>
          </ul>
        </div>
      </div>
    </template>

    <!-- Apply-fixes confirmation -->
    <div v-if="applyModal.show" class="aiw2-overlay" @click.self="applyModal.show = false">
      <div class="aiw2-modal">
        <div class="modal-head">
          <h3>Apply {{ selectedCount }} fix{{ selectedCount === 1 ? '' : 'es' }} in one run?</h3>
          <button class="close" aria-label="Close" @click="applyModal.show = false">×</button>
        </div>
        <div class="modal-body">
          <p>POP will run <b>one</b> pass over the draft and apply the fixes you selected:</p>
          <ul class="modal-list">
            <li v-for="iss in selectedIssues" :key="iss.issue_id">
              <span class="ml-ico">✓</span>
              <span class="ml-title">{{ iss.title }}</span>
              <span class="ml-cost" :class="{ free: !iss.cost }">{{ iss.cost ? '1 cr' : 'free' }}</span>
            </li>
          </ul>
          <div class="modal-cost">
            <span>⚡</span> Total <strong>{{ costLabel }}</strong><template v-if="unlimitedRegen"> · free on your account</template><template v-else-if="ownKeyFree && creditCount"> · uses your {{ freeRemaining }} free first · free after that with your own API key</template><template v-else-if="creditCount"> · charged once · uses your {{ freeRemaining }} free first</template><template v-else> · charged once</template>
          </div>
          <p class="modal-note">Batched into a single run so you're charged once. Locked sections and your edits are preserved.</p>
          <div v-if="fixError" class="fix-error">{{ fixError }}</div>
        </div>
        <div class="modal-foot">
          <button class="seo-btn btn-soft btn-sm" @click="applyModal.show = false">Cancel</button>
          <button class="seo-btn btn-primary btn-sm" :disabled="busy" @click="confirmApply">Apply fixes</button>
        </div>
      </div>
    </div>

    <!-- diff modal -->
    <div v-if="diffModal.show" class="aiw2-overlay" @click.self="diffModal.show = false">
      <div class="aiw2-modal wide">
        <div class="modal-head">
          <h3>Before &amp; after</h3>
          <button class="close" aria-label="Close" @click="diffModal.show = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="!diffModal.rows.length" class="help-text">No changes to show.</div>
          <div v-for="(d, i) in diffModal.rows" :key="i" class="diff-row">
            <div class="diff-head">{{ d.change_type }} · {{ d.summary }}</div>
            <div class="diff-cols">
              <div class="diff-col before" v-html="d.before_html || '<em>-</em>'"></div>
              <div class="diff-col after" v-html="d.after_html || '<em>-</em>'"></div>
            </div>
          </div>
        </div>
        <div class="modal-foot">
          <button class="seo-btn btn-soft btn-sm" @click="diffModal.show = false">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Hero from "../../components/Hero.vue";
import ScoreCard from "./ScoreCard.vue";
import IssueCard from "./IssueCard.vue";
import {
  aiWriterAnalyze, aiWriterFix, aiWriterDiff,
  aiWriterContinueToPublish, aiWriterGetProgress, aiWriterGetState,
} from "@/api";

const POLISH_STYLES = [
  { value: "score_safe", label: "Score-safe polish", desc: "Preserves SEO terms and structure. Only adjusts transitions and weak phrasing." },
  { value: "premium_editorial", label: "Premium editorial polish", desc: "Improves flow, rhythm, and voice more aggressively. May shift exact phrasing." },
  { value: "readability_first", label: "Readability-first polish", desc: "Makes content smoother and friendlier. Score may shift slightly." },
];

const PRESETS = [
  { key: "all", label: "All" },
  { key: "seo", label: "SEO only" },
  { key: "quality", label: "Quality only" },
  { key: "free", label: "Free only" },
];

export default {
  name: "AiwV2SeoQualityStep",
  components: { Hero, ScoreCard, IssueCard },
  props: {
    aiRunId: { default: null },
    keyword: { type: String, default: "" },
    mode: { type: String, default: "" },
    pageRun: { type: Object, default: () => ({}) },
    initialSeoQuality: { type: Object, default: null },
    // Regenerate-credit context (free pool + white-glove), from session.credits.
    credits: { type: Object, default: () => ({}) },
  },
  data() {
    const sq = this.initialSeoQuality || null;
    return {
      POLISH_STYLES,
      PRESETS,
      sq,
      analyzing: false,
      busy: false,
      polishStyle: (sq && sq.polish_style) || "score_safe",
      selected: [],
      applyModal: { show: false },
      diffModal: { show: false, rows: [] },
      fixApplying: false,
      fixProgress: { pct: 0, label: "" },
      // Snapshot of the fixes in the current run (progress steps) + the fixes
      // resolved by the last run (shown as done/locked cards).
      runSnapshot: [],
      fixedSnapshot: [],
      fixError: "",
      _poll: null,
    };
  },
  computed: {
    token() {
      return this.$store.getters.getToken;
    },
    scores() {
      return (this.sq && this.sq.scores) || {};
    },
    metaItems() {
      const wc = this.scores.word_count;
      if (!wc) return [];
      return [`Draft generated · ${Number(wc).toLocaleString("en-US")} words`];
    },
    popScore() {
      return this.scores.pop_score != null ? Math.round(this.scores.pop_score) : null;
    },
    pageExp() {
      return this.scores.page_experience_score != null ? this.scores.page_experience_score : null;
    },
    popColor() {
      if (this.popScore == null) return "amber";
      if (this.popScore >= (this.scores.target_pop_score || 100)) return "green";
      return this.popScore >= 70 ? "amber" : "orange";
    },
    pageColor() {
      if (this.pageExp == null) return "amber";
      if (this.pageExp >= 90) return "green";
      return this.pageExp >= 70 ? "amber" : "orange";
    },
    popMeta() {
      const t = this.scores.target_pop_score || 100;
      return `${this.lastFix ? "After fixes" : "Before fixes"} · target <strong>${t}</strong>`;
    },
    pageExpMeta() {
      if (this.pageExp == null) return "";
      return this.pageExp >= 90 ? "Beautiful, scannable, readable" : "Good, but needs polish";
    },
    whatsWorking() {
      const s = this.scores;
      const out = [];
      if (s.pop_score != null && s.pop_score >= (s.target_pop_score || 100) - 6) out.push("On track for your POP score target");
      else if (s.pop_score != null && s.pop_score >= 70) out.push("Solid keyword and term coverage");
      if (this.pageExp != null && this.pageExp >= 85) out.push("Clean, scannable page experience");
      if (s.word_count && s.target_word_count && s.word_count >= s.target_word_count) out.push("Meets your target word count");
      out.push("Structured with a clear intro and sections");
      return out.slice(0, 4);
    },
    needsImprovement() {
      return ((this.sq && this.sq.issues) || [])
        .filter((i) => !i.ignored && i.category === "quality")
        .map((i) => i.title);
    },
    barState() {
      return {
        ctaLabel: "Continue to Publish →",
        disabled: this.analyzing,
        busy: this.busy,
        hideNext: false,
      };
    },
    // The runnable fixes — one selectable card each.
    fixableIssues() {
      return ((this.sq && this.sq.issues) || []).filter((i) => i.fixable && !i.ignored);
    },
    selectedIssues() {
      return this.fixableIssues.filter((i) => this.isSelected(i.issue_id));
    },
    selectedCount() {
      return this.selectedIssues.length;
    },
    // Total credits = selected fixes that aren't free (cost 1).
    creditCount() {
      return this.selectedIssues.reduce((n, i) => n + (i.cost ? 1 : 0), 0);
    },
    // Credits actually charged: 0 for white-glove/unlimited accounts and own-key
    // runs (free beyond the pool) or when the free regenerate pool covers the
    // selection; otherwise the amount beyond it.
    paidCredits() {
      if (this.unlimitedRegen || this.ownKeyFree) return 0;
      return Math.max(0, this.creditCount - this.freeRemaining);
    },
    costLabel() {
      return this.paidCredits === 0 ? "free" : `${this.paidCredits} credit${this.paidCredits === 1 ? "" : "s"}`;
    },
    // Fix-bar button label: "Apply N fixes · free|X credits" (or a prompt to select).
    applyLabel() {
      if (!this.selectedCount) return "Select a fix to continue";
      const n = this.selectedCount;
      return `Apply ${n} fix${n === 1 ? "" : "es"} · ${this.costLabel}`;
    },
    // Cards to render: the runnable fixes (selectable) plus any just-fixed issues
    // (resolved by the last run) shown as done/locked.
    displayIssues() {
      const currentIds = new Set(this.fixableIssues.map((i) => i.issue_id));
      const out = this.fixableIssues.map((i) => ({ issue: i, fixed: false }));
      this.fixedSnapshot.forEach((i) => {
        if (!currentIds.has(i.issue_id)) out.push({ issue: i, fixed: true });
      });
      return out;
    },
    // Titles of the fixes in the current/last run — shown as the progress steps.
    fixSteps() {
      return this.runSnapshot.map((i) => i.title);
    },
    unlimitedRegen() {
      return !!(this.credits && this.credits.unlimitedRegenerate);
    },
    // Own-key runs consume the free pool first, then stay free.
    ownKeyFree() {
      return !!(this.credits && this.credits.ownKeyFree);
    },
    freeRemaining() {
      return this.credits && this.credits.regenerateRemaining != null ? this.credits.regenerateRemaining : 5;
    },
    // SEO-fix run counter (informational): runs beyond the included count stay
    // available at the normal per-fix credit cost.
    fixRunsUsed() {
      return (this.credits && this.credits.fixRunsUsed) || 0;
    },
    fixRunLimit() {
      return (this.credits && this.credits.fixRunLimit) || 2;
    },
    fixRunsPct() {
      return Math.min(100, Math.max(0, (this.fixRunsUsed / this.fixRunLimit) * 100));
    },
    lastFix() {
      return this.sq && this.sq.last_fix;
    },
  },
  watch: {
    barState: { immediate: true, handler(v) { this.$emit("bar", v); } },
  },
  async mounted() {
    if (!this.sq || !this.sq.analyzed_at) {
      await this.analyze();
    } else {
      this.resetSelection();
    }
  },
  beforeDestroy() {
    if (this._poll) clearTimeout(this._poll);
  },
  methods: {
    barNext() {
      this.continueToPublish();
    },
    // Poll the given phase; update fixProgress live so the bar animates, resolve on
    // complete/failed.
    pollPhase(phase) {
      return new Promise((resolve) => {
        const tick = async () => {
          try {
            const res = await aiWriterGetProgress(this.token, this.aiRunId);
            const p = res?.data?.progress || {};
            if (p.phase === phase) {
              if (p.pct != null) this.fixProgress = { pct: p.pct, label: p.label || this.fixProgress.label };
              if (p.status === "complete" || p.status === "failed") return resolve(p);
            }
          } catch (e) { /* keep polling */ }
          this._poll = setTimeout(tick, 1300);
        };
        this._poll = setTimeout(tick, 800);
      });
    },
    async reloadSeoQuality() {
      try {
        const res = await aiWriterGetState(this.token, this.aiRunId);
        if (res?.data?.seoQuality !== undefined) {
          this.sq = res.data.seoQuality;
          this.$emit("analyzed", this.sq);
          this.resetSelection();
        }
      } catch (e) { /* ignore */ }
    },
    // Default to every runnable fix selected.
    resetSelection() {
      this.selected = this.fixableIssues.map((i) => i.issue_id);
    },
    isSelected(id) {
      return this.selected.indexOf(id) !== -1;
    },
    toggleSelect(issue) {
      const i = this.selected.indexOf(issue.issue_id);
      if (i !== -1) this.selected.splice(i, 1);
      else this.selected.push(issue.issue_id);
    },
    applyPreset(key) {
      let list = this.fixableIssues;
      if (key === "seo") list = list.filter((i) => i.category === "seo");
      else if (key === "quality") list = list.filter((i) => i.category === "quality");
      else if (key === "free") list = list.filter((i) => !i.cost);
      this.selected = list.map((i) => i.issue_id);
    },
    async analyze() {
      this.analyzing = true;
      try {
        const res = await aiWriterAnalyze(this.token, this.aiRunId);
        if (res?.data?.status === "SUCCESS") {
          await this.pollPhase("analyze");
          await this.reloadSeoQuality();
        }
      } finally {
        this.analyzing = false;
      }
    },
    openApply() {
      if (!this.selectedCount) return;
      this.fixError = "";
      this.applyModal = { show: true };
    },
    async confirmApply() {
      this.applyModal.show = false;
      await this.runFix();
    },
    // Progress-step state from the overall fix pct: done / doing / pending.
    stepState(i) {
      if (this.fixProgress.pct >= 100) return "done";
      const done = Math.floor((this.fixSteps.length * this.fixProgress.pct) / 100);
      if (i < done) return "done";
      if (i === done) return "doing";
      return "pending";
    },
    async runFix() {
      this.busy = true;
      this.fixError = "";
      this.runSnapshot = this.selectedIssues.slice();
      this.fixProgress = { pct: 0, label: "Reconciling SEO terms" };
      this.fixApplying = true;
      try {
        const res = await aiWriterFix(this.token, this.aiRunId, {
          scope: "selected",
          issueIds: this.selected.slice(),
          polishStyle: this.polishStyle,
          confirm: true,
        });
        if (res?.data?.status === "SUCCESS") {
          await this.pollPhase("fix");
          const snapshot = this.runSnapshot.slice();
          await this.reloadSeoQuality();
          this.fixedSnapshot = snapshot;
          this.$emit("credits-changed");
        } else if (res?.data?.code === "insufficient_credits") {
          this.fixError = "You don't have enough credits to apply these fixes.";
          this.applyModal = { show: true };
        } else {
          this.fixError = res?.data?.error || "Could not apply fixes.";
          this.applyModal = { show: true };
        }
      } catch (e) {
        this.fixError = "Could not apply fixes.";
        this.applyModal = { show: true };
      } finally {
        this.busy = false;
        this.fixApplying = false;
      }
    },
    async openDiff() {
      this.diffModal = { show: true, rows: [] };
      try {
        const res = await aiWriterDiff(this.token, this.aiRunId);
        this.diffModal.rows = res?.data?.diff || [];
      } catch (e) { /* ignore */ }
    },
    async continueToPublish() {
      this.busy = true;
      try {
        const res = await aiWriterContinueToPublish(this.token, this.aiRunId);
        if (res?.data?.status === "SUCCESS") this.$emit("continue");
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.aiw2-seo { margin: 0 auto; }
.intro { font-size: 13px; color: var(--text-3, #6b7b7e); margin-bottom: 14px; }
.loading-card { display: flex; align-items: center; gap: 10px; justify-content: center; color: var(--text-3, #6b7b7e); padding: 24px; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); }

/* Score strip */
.score-strip { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 18px; }
@media (max-width: 720px) { .score-strip { grid-template-columns: 1fr; } }

/* Protect banner */
.protect-banner { background: #e9f3ff; border: 1px solid #c7d5f9; border-radius: 8px; padding: 10px 14px; margin: 10px 0 14px; font-size: 12.5px; color: var(--pop-blue, #316bff); display: flex; align-items: center; gap: 9px; }
.protect-banner .ico { font-size: 14px; }

/* Applying-fixes progress (1:1 with the design's .fixprog) */
.fixprog { border: 1px solid var(--border, #e4e4e4); background: #fff; border-radius: var(--r-lg, 10px); padding: 16px 18px 15px; margin-bottom: 14px; }
.fixprog-head { display: flex; align-items: center; gap: 9px; margin-bottom: 4px; }
.fixprog-head h4 { margin: 0; font-size: 14.5px; font-weight: 800; color: var(--text, #052e33); }
.fixprog-pct { margin-left: auto; font-size: 13px; font-weight: 800; color: var(--pop-orange-deep, #b23d00); font-variant-numeric: tabular-nums; }
.fixprog-sub { font-size: 12px; color: var(--text-2, #014751); margin-bottom: 11px; }
.fixprog-track { height: 7px; border-radius: 100px; background: var(--surface-2, #f7f7f7); overflow: hidden; }
.fixprog-fill { height: 100%; background: var(--pop-orange, #ff5700); border-radius: 100px; transition: width .45s cubic-bezier(.4,0,.2,1); }
.fixprog-steps { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 7px; }
.fixprog-steps li { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: var(--text-3, #6b7b7e); }
.fixprog-steps li.done { color: var(--text-2, #014751); }
.fixprog-steps li.doing { color: var(--text, #052e33); font-weight: 600; }
.fixprog-steps li .dot { flex: none; width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid var(--border, #e4e4e4); background: #fff; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; color: #fff; }
.fixprog-steps li.done .dot { background: var(--pop-green, #25a97a); border-color: var(--pop-green, #25a97a); }
.fixprog-steps li.done .dot::before { content: "✓"; }
.fixprog-steps li.doing .dot { border-color: var(--pop-orange, #ff5700); border-right-color: transparent; animation: fpSpin .7s linear infinite; }
@keyframes fpSpin { to { transform: rotate(360deg); } }

/* Section label */
.section-label { font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin-bottom: 10px; }
.issue-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }

/* Polish style */
.polish { margin-top: 22px; }
.polish-title { font-size: 13.5px; font-weight: 600; margin-bottom: 4px; color: var(--text, #052e33); }
.help-text { font-size: 12px; color: var(--text-3, #6b7b7e); margin-bottom: 4px; line-height: 1.5; }
.polish-style-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin: 12px 0; }
.polish-style-card { background: var(--surface, #fff); border: 1.5px solid var(--border, #e4e4e4); border-radius: 10px; padding: 12px 14px; cursor: pointer; font-family: inherit; text-align: left; transition: all .15s; position: relative; }
.polish-style-card:hover { border-color: var(--border-strong, #ccc); }
.polish-style-card.selected { border-color: var(--pop-orange, #ff5700); background: var(--pop-orange-soft, #ffeddd); }
.polish-style-card .name { font-size: 13px; font-weight: 600; color: var(--text, #052e33); margin-bottom: 3px; }
.polish-style-card .desc { font-size: 11.5px; color: var(--text-3, #6b7b7e); line-height: 1.45; }
.polish-style-card .rec-tag { position: absolute; top: -8px; right: 10px; background: var(--pop-green-soft, #d6fbee); color: #047857; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 999px; border: 1px solid #c8efd9; }

/* Fix bar */
.fix-bar { margin-top: 16px; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.fix-bar-sum { font-size: 13.5px; color: var(--text, #052e33); }
.fix-bar-sum b { font-weight: 800; }
.fix-budget { display: flex; align-items: center; gap: 8px; font-size: 11.5px; color: var(--text-3, #6b7b7e); margin-top: 6px; }
.fix-budget b { color: var(--text, #052e33); font-weight: 700; }
.fix-budget-track { width: 90px; height: 4px; border-radius: 999px; background: var(--surface-2, #f7f7f7); overflow: hidden; }
.fix-budget-fill { height: 100%; border-radius: 999px; background: var(--pop-orange, #ff5700); }
.fix-bar-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.fix-presets { display: inline-flex; gap: 6px; flex-wrap: wrap; }
.fix-preset { font-family: inherit; font-size: 11.5px; font-weight: 600; color: var(--text-2, #014751); background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 100px; padding: 5px 11px; cursor: pointer; transition: all .12s; }
.fix-preset:hover:not(:disabled) { border-color: var(--pop-orange, #ff5700); color: var(--pop-orange-deep, #b23d00); }
.fix-preset:disabled { opacity: .55; cursor: default; }
.all-good { color: var(--pop-green, #25a97a); font-weight: 600; font-size: 13.5px; background: var(--pop-green-soft, #d6fbee); border: 1px solid #c8efd9; border-radius: var(--r-lg, 10px); padding: 14px 18px; display: flex; align-items: center; gap: 8px; }

/* Applied card */
.applied-card { background: linear-gradient(135deg, var(--pop-green-soft, #d6fbee) 0%, #fff 100%); border: 1px solid #c8efd9; border-radius: var(--r-lg, 10px); padding: 16px 20px; margin: 12px 0; }
.applied-card h4 { margin: 0 0 8px; font-size: 14.5px; font-weight: 700; color: #047857; }
.applied-list { list-style: none; padding: 0; margin: 0; font-size: 13px; }
.applied-list li { padding: 3px 0; color: #047857; display: flex; align-items: center; gap: 6px; }
.applied-list li .ico { color: var(--pop-green, #25a97a); font-weight: 700; }
.view-diff { margin-top: 10px; }

/* Quality review */
.quality-head { font-size: 16px; font-weight: 700; margin: 24px 0 10px; color: var(--text, #052e33); }
.quality-card { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 18px 22px; margin-bottom: 12px; }
.quality-section { margin-bottom: 12px; }
.quality-section:last-child { margin-bottom: 0; }
.quality-section h5 { font-size: 10.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; margin: 0 0 8px; }
.quality-section h5.good { color: var(--pop-green, #25a97a); }
.quality-section h5.work { color: var(--pop-amber, #aa993b); }
.quality-list { list-style: none; padding: 0; margin: 0; }
.quality-list li { display: flex; align-items: flex-start; gap: 10px; padding: 4px 0; font-size: 13.5px; color: var(--text-2, #014751); }
.quality-list li .ico { flex-shrink: 0; padding-top: 2px; font-weight: 700; }
.quality-list li .ico.good { color: var(--pop-green, #25a97a); }
.quality-list li .ico.work { color: var(--pop-amber, #aa993b); }

/* Buttons */
.seo-btn { font-family: inherit; cursor: pointer; border-radius: 7px; font-weight: 600; font-size: 13px; padding: 9px 18px; display: inline-flex; align-items: center; gap: 6px; transition: all .15s; border: none; }
.seo-btn:disabled { opacity: .6; cursor: not-allowed; }
.btn-primary { background: var(--pop-orange, #ff5700); color: #fff; box-shadow: 0 2px 6px rgba(255,87,0,.25); }
.btn-primary:hover:not(:disabled) { background: var(--pop-orange-hover, #e64e00); }
.btn-fix-all { background: #fff; color: var(--pop-orange-deep, #b23d00); border: 1.5px solid var(--pop-orange, #ff5700); box-shadow: none; }
.btn-fix-all:hover:not(:disabled) { background: var(--pop-orange-soft, #ffeddd); }
.btn-fix-all:disabled { opacity: .6; cursor: default; }
.btn-soft { background: #fff; color: var(--text, #052e33); border: 1px solid var(--border-strong, #ccc); }
.btn-soft:hover { background: var(--surface-2, #f7f7f7); }
.btn-xs { padding: 4px 9px; font-size: 11.5px; }

/* Modals — 1:1 with the design's .modal-backdrop / .modal shell (kept on the
   Bulma-safe .aiw2-* class names). */
.aiw2-overlay { position: fixed; inset: 0; background: rgba(15,20,25,.45); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 20px; }
.aiw2-modal { background: var(--surface, #fff); border-radius: var(--r-xl, 14px); max-width: 560px; width: 100%; box-shadow: var(--shadow-lg, 0 24px 70px rgba(0,0,0,.35)); max-height: 90vh; overflow: auto; font-family: "Mulish", system-ui, sans-serif; }
.aiw2-modal.wide { max-width: 760px; }
.modal-head { padding: 20px 24px 8px; display: flex; align-items: flex-start; gap: 12px; }
.modal-head h3 { margin: 0; font-size: 17px; font-weight: 800; flex: 1; color: var(--text, #052e33); }
.close { background: none; border: none; color: var(--text-3, #6b7b7e); cursor: pointer; font-size: 22px; padding: 0; line-height: 1; }
.close:hover { color: var(--text, #052e33); }
.modal-body { padding: 4px 24px 8px; font-size: 13.5px; color: var(--text-2, #014751); }
.modal-body p { margin: 6px 0; line-height: 1.5; }
.modal-note { color: var(--text-3, #6b7b7e); font-size: 12px; margin-top: 8px; }
.modal-list { list-style: none; padding: 0; margin: 8px 0; max-height: 40vh; overflow: auto; }
.modal-list li { padding: 6px 0; display: flex; align-items: center; gap: 9px; color: var(--text-2, #014751); }
.modal-list .ml-ico { color: var(--pop-green, #25a97a); font-weight: 700; flex-shrink: 0; }
.modal-list .ml-title { flex: 1; min-width: 0; }
.modal-list .ml-cost { flex-shrink: 0; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 100px; background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); }
.modal-list .ml-cost.free { background: var(--pop-green-soft, #e0f5ec); color: var(--pop-green, #25a97a); }
.modal-cost { margin: 10px 0 4px; padding: 10px 12px; background: var(--pop-orange-soft, #ffeddd); border: 1px solid var(--pop-orange-tint, #ffd9c2); border-radius: 8px; font-size: 13px; color: var(--pop-orange-deep, #b23d00); font-weight: 600; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.modal-cost strong { color: inherit; }
.fix-error { color: var(--pop-red, #ff3939); font-size: 12.5px; margin-top: 10px; }
.modal-foot { padding: 14px 24px 20px; display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid var(--border, #e4e4e4); margin-top: 12px; }
.btn-sm { padding: 6px 14px; font-size: 12.5px; }
.diff-row { border: 1px solid var(--border, #e4e4e4); border-radius: var(--r, 8px); padding: 10px; margin-bottom: 10px; }
.diff-head { font-size: 11.5px; color: var(--text-3, #6b7b7e); margin-bottom: 6px; text-transform: capitalize; }
.diff-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.diff-col { font-size: 12.5px; padding: 8px; border-radius: 6px; }
.diff-col.before { background: #fff5f5; }
.diff-col.after { background: #f1fbf6; }
.spin { width: 16px; height: 16px; border: 2px solid var(--border, #e4e4e4); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; display: inline-block; animation: aiw2-seo-spin .7s linear infinite; }
@keyframes aiw2-seo-spin { to { transform: rotate(360deg); } }
</style>
